import { useMap } from "@/shared/ui/map";
import type { GeoJSONSource } from "maplibre-gl";
import { useEffect } from "react";
import { createCirclePolygon } from "./map-geo-utils";

export type BranchCircleSpec = {
  id: string;
  lng: number;
  lat: number;
  radiusM: number;
  /** Highlight selected branch */
  selected?: boolean;
};

type BranchMapCircleLayersProps = {
  circles: BranchCircleSpec[];
};

export function BranchMapCircleLayers({ circles }: BranchMapCircleLayersProps) {
  const { map, isLoaded } = useMap();

  useEffect(() => {
    if (!map || !isLoaded) return;

    const sourceId = "branch-radius-circles";
    const fillLayerId = `${sourceId}-fill`;
    const lineLayerId = `${sourceId}-line`;

    const features = circles
      .filter((c) => c.radiusM > 0)
      .map((c) => {
        const feature = createCirclePolygon(c.lng, c.lat, c.radiusM);
        feature.properties = {
          branchId: c.id,
          selected: c.selected ?? false,
        };
        return feature;
      });

    const geojson: GeoJSON.FeatureCollection = {
      type: "FeatureCollection",
      features,
    };

    const addLayers = () => {
      if (map.getSource(sourceId)) {
        (map.getSource(sourceId) as GeoJSONSource).setData(geojson);
        return;
      }

      map.addSource(sourceId, {
        type: "geojson",
        data: geojson,
      });

      map.addLayer({
        id: fillLayerId,
        type: "fill",
        source: sourceId,
        paint: {
          "fill-color": [
            "case",
            ["==", ["get", "selected"], true],
            "#2563eb",
            "#64748b",
          ],
          "fill-opacity": 0.12,
        },
      });

      map.addLayer({
        id: lineLayerId,
        type: "line",
        source: sourceId,
        paint: {
          "line-color": [
            "case",
            ["==", ["get", "selected"], true],
            "#2563eb",
            "#64748b",
          ],
          "line-width": 2,
          "line-opacity": 0.7,
        },
      });
    };

    if (map.isStyleLoaded()) {
      addLayers();
    } else {
      map.once("styledata", addLayers);
    }

    return () => {
      if (!map.isStyleLoaded()) return;
      if (map.getLayer(lineLayerId)) map.removeLayer(lineLayerId);
      if (map.getLayer(fillLayerId)) map.removeLayer(fillLayerId);
      if (map.getSource(sourceId)) map.removeSource(sourceId);
    };
  }, [map, isLoaded, circles]);

  return null;
}
